import { useCallback } from 'react';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/nextjs';
// Types
import { FbAuth_fbAuth } from 'api/auth/types/FbAuth';
import { GoogleAuth_googleAuth } from 'api/auth/types/GoogleAuth';
import { TwitterAuth_twitterAuth } from 'api/auth/types/TwitterAuth';
import { TiktokV2Auth_tiktokV2Auth } from 'api/auth/types/TiktokV2Auth';
import { OTPType } from 'context/OnboardingContext';
// Helpers
import { getProperErrorMessage } from 'helpers/errors';
import {
  getRedirectFromLocalStorage,
  clearRedirectFromLocalStorage,
  getSignUpInviteCodeFromLocalStorage,
  setFullOnboardingLocalStorageFlag,
} from 'helpers/storage';
import { handleFirstLogin } from 'helpers/user';
import { canOpenSignUpModalForSocial } from 'helpers/socialSignUp';
import {
  clearExtTalentBdReturnUrlFromStorage,
  getExtTalentBdReturnUrlFromStorage,
} from 'helpers/extTalentBrandDeal';
import { isStoreRole } from 'helpers/user';
// Hooks
import { useAppContext } from 'hooks/useAppContext';
import { useOnboardingContext } from 'hooks/useOnboardingContext';
import { useRedeemInvCode } from 'hooks/useReedemInvCode';
// Components
import { showToast } from 'components/common/Toast/Toast';

export type SocialAuthResponse =
  | FbAuth_fbAuth
  | GoogleAuth_googleAuth
  | TwitterAuth_twitterAuth
  | TiktokV2Auth_tiktokV2Auth;

type HandleResponseOptions = {
  /** Facebook's redirect login leaves the token in the URL instead of clearing it. */
  resetQueryWhenNoRedirect?: boolean;
};

type UseSocialAuth = {
  handleSocialAuthResponse: (
    response: SocialAuthResponse,
    options?: HandleResponseOptions
  ) => Promise<void>;
  handleSocialAuthError: (error: unknown, provider: string) => void;
};

/**
 * Shared tail of every social login: the four provider watchers differ only in
 * how they obtain the auth response, not in what they do with it.
 */
export const useSocialAuth = (): UseSocialAuth => {
  const { pathname, push } = useRouter();
  const {
    setToken,
    setIsSocialSignUp,
    setSocialSignUpData,
    setSignUpModalVisibility,
  } = useAppContext();
  const {
    setOTPValidation,
    setFullOnboarding,
    setShowOnboardingModal,
  } = useOnboardingContext();
  const { tryToRedeemInviteCode } = useRedeemInvCode();

  const handleSocialAuthResponse = useCallback(
    async (
      response: SocialAuthResponse,
      { resetQueryWhenNoRedirect = true }: HandleResponseOptions = {}
    ): Promise<void> => {
      const redirectPath = getRedirectFromLocalStorage();
      const campaignUrl = getExtTalentBdReturnUrlFromStorage();
      let inviteRedirected = false;
      let offerCreatorUpgrade = false;

      if (response.__typename === 'SocialSignUpResponse') {
        if (canOpenSignUpModalForSocial(pathname, redirectPath)) {
          setSignUpModalVisibility(true);
        }
        setIsSocialSignUp(true);
        setSocialSignUpData(response);
        setOTPValidation({
          type: OTPType.Social,
          id: '',
          hasEmail: !!response.socialSession.email,
        });

        if (getSignUpInviteCodeFromLocalStorage()) {
          setFullOnboarding(true);
          setFullOnboardingLocalStorageFlag('true');
        }
      } else {
        setToken({
          id: response.userId,
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          role: response.user?.role,
        });

        const dest = await tryToRedeemInviteCode(false, response.user?.role);
        handleFirstLogin(response.user?.role);
        inviteRedirected = !!dest;

        // Signing in skips signup, so nothing has offered this talent the
        // creator upgrade the campaign invite depends on. Anyone already a
        // creator is left alone.
        offerCreatorUpgrade =
          !!campaignUrl && !isStoreRole(response.user?.role);
      }

      if (inviteRedirected) {
        // useRedeemInvCode already navigated to the deal; don't override it.
        return;
      }

      // The campaign link wins over the generic redirect: the provider callback
      // replaced the URL, and this is the one the talent actually arrived on.
      if (campaignUrl) {
        await push(campaignUrl);
        clearExtTalentBdReturnUrlFromStorage();
        clearRedirectFromLocalStorage();

        if (offerCreatorUpgrade) {
          setShowOnboardingModal(true);
        }
        return;
      }

      if (redirectPath) {
        await push(redirectPath);
        clearRedirectFromLocalStorage();
        return;
      }

      if (resetQueryWhenNoRedirect) {
        await push({ query: '' });
      }
    },
    [
      pathname,
      push,
      setFullOnboarding,
      setIsSocialSignUp,
      setOTPValidation,
      setShowOnboardingModal,
      setSignUpModalVisibility,
      setSocialSignUpData,
      setToken,
      tryToRedeemInviteCode,
    ]
  );

  const handleSocialAuthError = useCallback(
    (error: unknown, provider: string): void => {
      push({ query: '' });

      showToast({
        message: getProperErrorMessage(
          error,
          'Looks like something went wrong. Please try again later.'
        ),
        type: 'error',
      });

      Sentry.captureMessage(`${provider} sign up: ${JSON.stringify(error)}`, {
        tags: {
          section: 'auth',
        },
      });
    },
    [push]
  );

  return { handleSocialAuthResponse, handleSocialAuthError };
};
